import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CATEGORIES, FREQUENCIES, STATUSES } from "@/lib/subscriptions";
import { useSubs } from "@/context/SubsContext";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  category: "streaming",
  price: "",
  frequency: "mensuelle",
  next_renewal: "",
  status: "actif",
  note: "",
};

export default function SubscriptionDialog({ open, onOpenChange, initial }) {
  const { addSub, updateSub } = useSubs();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial, price: String(initial.price) } : EMPTY);
    }
  }, [open, initial]);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Le nom est requis");
    const price = parseFloat(String(form.price).replace(",", "."));
    if (!price || price <= 0) return toast.error("Prix invalide");
    if (!form.next_renewal) return toast.error("Date de renouvellement requise");

    const payload = {
      name: form.name.trim(),
      category: form.category,
      price,
      frequency: form.frequency,
      next_renewal: form.next_renewal,
      status: form.status,
      note: form.note || "",
      is_demo: false,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await updateSub(initial.id, payload);
      } else {
        await addSub(payload);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] rounded-2xl" data-testid="sub-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            {isEdit ? "Modifier l'abonnement" : "Nouvel abonnement"}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Renseignez les détails ci-dessous. Les totaux sont recalculés automatiquement.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nom du service</Label>
            <Input
              id="name"
              data-testid="sub-name-input"
              placeholder="Netflix, Spotify, etc."
              value={form.name}
              onChange={handleChange("name")}
              className="rounded-xl focus-visible:ring-emerald-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={handleChange("category")}>
                <SelectTrigger data-testid="sub-category-select" className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Fréquence</Label>
              <Select value={form.frequency} onValueChange={handleChange("frequency")}>
                <SelectTrigger data-testid="sub-frequency-select" className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="price">Prix (€)</Label>
              <Input
                id="price"
                data-testid="sub-price-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="9,99"
                value={form.price}
                onChange={handleChange("price")}
                className="rounded-xl focus-visible:ring-emerald-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="renewal">Prochain renouvellement</Label>
              <Input
                id="renewal"
                data-testid="sub-renewal-input"
                type="date"
                value={form.next_renewal}
                onChange={handleChange("next_renewal")}
                className="rounded-xl focus-visible:ring-emerald-500"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Statut</Label>
            <Select value={form.status} onValueChange={handleChange("status")}>
              <SelectTrigger data-testid="sub-status-select" className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="note">Note (facultatif)</Label>
            <Textarea
              id="note"
              data-testid="sub-note-input"
              placeholder="Partagé avec famille, à surveiller…"
              value={form.note}
              onChange={handleChange("note")}
              className="rounded-xl focus-visible:ring-emerald-500"
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="sub-cancel-btn"
              className="rounded-full"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="sub-submit-btn"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
